import { FC, useCallback, useEffect, useState } from 'react'
import {
  Datagrid, DateField, DeleteWithConfirmButton, EditButton,
  FunctionField, ListContextProvider, Pagination,
  Show, TabbedShowLayout,
  TextField, TopToolbar, useRecordContext, useGetList, useList,
  useTranslate, useNotify, Tab,
} from 'react-admin'
import {
  Alert, Box, Chip, CircularProgress, IconButton, Menu, MenuItem,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  DetailCard, EnabledChip, InfoRow, LOG_METHOD_COLORS, LogBodyPreview, LogsChart,
  McpLinksDatagrid, MetadataTable, StatCard, TierChip, UsageChart, WorkflowLinksDatagrid, formatDateTime,
} from '../../components'
import { border } from '../../layout/theme/border'
import { xxllncColor } from '../../layout/theme/color'
import { fadeSlideIn } from '../../components/dashboard'
import {
  AutomationRuleOut,
  OrgAuth0MemberOut,
  OrganizationOut,
  PortalSettingsOut,
} from '../../types/apiRequests'
import { McpUsageLogOut } from '../../types/localTypes'
import { WorkflowExecutionOut } from '../../types/apiRequests'
import OrgOpenWebuiConfigTab from '../OrgOpenWebuiConfig/OrgOpenWebuiConfigTab'
import OrganizationIdpTab from '../OrganizationIdp/OrganizationIdpTab'
import { deleteJson, getJson, getUrl, postJson } from '../../providers/data'

const ShowActions: FC = () => {
  const translate = useTranslate()
  return (
    <TopToolbar>
      <EditButton />
      <DeleteWithConfirmButton
        confirmTitle={translate('delete.confirmTitle')}
        confirmContent={translate('delete.confirmContentOrganization')}
      />
    </TopToolbar>
  )
}

const LinksTab: FC = () => {
  const record = useRecordContext<OrganizationOut>()
  const translate = useTranslate()
  if (!record?.id) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {translate('resources.organization.tabs.mcpLinks')}
        </Typography>
        <McpLinksDatagrid
          // eslint-disable-next-line camelcase
          filter={{ organization_id: record.id }}
          // eslint-disable-next-line camelcase
          createState={{ organization_id: record.id }}
          showServer
        />
      </Box>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {translate('resources.organization.tabs.workflowLinks')}
        </Typography>
        <WorkflowLinksDatagrid
          // eslint-disable-next-line camelcase
          filter={{ organization_id: record.id }}
          // eslint-disable-next-line camelcase
          createState={{ organization_id: record.id }}
          showWorkflow
        />
      </Box>
    </Box>
  )
}

const UsageTab: FC = () => {
  const record = useRecordContext<OrganizationOut>()
  if (!record?.id) return null

  return <UsageChart organizationId={record.id} showCosts={false} />
}

const ScopeChip: FC<{ scope: string }> = ({ scope }) => {
  const isGlobal = scope === 'global'
  return (
    <Chip
      label={isGlobal ? 'Global' : 'Organization'}
      size="small"
      sx={{
        fontWeight: 600, fontSize: 11, height: 22, border: 'none',
        bgcolor: isGlobal ? xxllncColor.opDark10 : xxllncColor.opGreen10,
        color: isGlobal ? xxllncColor.dark100 : xxllncColor.green100,
      }}
    />
  )
}

const AutomationsTab: FC = () => {
  const record = useRecordContext<OrganizationOut>()

  const { data, isLoading } = useGetList<AutomationRuleOut>(
    'automation_rule',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'display_name', order: 'ASC' },
      // eslint-disable-next-line camelcase
      filter: { organization_id: record?.id },
    },
    { enabled: !!record?.id },
  )

  const listContext = useList({ data: data ?? [], isLoading, perPage: 25 })

  return (
    <ListContextProvider value={listContext}>
      <Datagrid bulkActionButtons={false} rowClick="show" resource="automation_rule">
        <TextField source="display_name" label="data.displayName" />
        <FunctionField<AutomationRuleOut>
          label="Scope"
          render={(rec) => <ScopeChip scope={rec.scope} />}
        />
        <FunctionField<AutomationRuleOut>
          label="Triggers"
          render={(rec) => (
            <Chip label={rec.triggers?.length ?? 0} size="small" sx={{ fontWeight: 600, fontSize: 11, height: 22 }} />
          )}
        />
        <FunctionField<AutomationRuleOut>
          label="data.actions"
          render={(rec) => (
            <Chip label={rec.actions?.length ?? 0} size="small" sx={{ fontWeight: 600, fontSize: 11, height: 22 }} />
          )}
        />
        <FunctionField label="data.enabled" render={() => <EnabledChip />} />
        <DateField source="last_triggered" showTime label="data.lastTriggered" />
      </Datagrid>
      <Pagination />
    </ListContextProvider>
  )
}

const LogRowBase: FC<{
  isSuccess: boolean
  created: string
  method: string
  responseTimeMs?: number | null
  nameCol: string
  subCol: string
  requestUrl?: string | null
  statusCode?: number | null
  errorMessage?: string | null
  responseBodyPreview?: string | null
}> = ({ isSuccess, created, method, responseTimeMs, nameCol, subCol, requestUrl, statusCode, errorMessage, responseBodyPreview }) => {
  const [expanded, setExpanded] = useState(false)
  const hasDetails = !isSuccess || !!requestUrl || !!responseBodyPreview
  // eslint-disable-next-line security/detect-object-injection
  const methodColor = LOG_METHOD_COLORS[method] ?? 'rgba(0,0,0,0.45)'

  return (
    <Box sx={{
      'borderBottom': '1px solid rgba(0,0,0,0.06)',
      'bgcolor': !isSuccess ? 'rgba(194,66,66,0.04)' : 'transparent',
      '&:hover': { bgcolor: !isSuccess ? 'rgba(194,66,66,0.08)' : 'rgba(0,0,0,0.02)' },
    }}>
      <Box
        sx={{
          display: 'grid', gridTemplateColumns: '140px 1fr 1fr 80px 1fr 70px 60px 32px',
          alignItems: 'center', px: 2, py: 1, gap: 1,
          cursor: hasDetails ? 'pointer' : 'default',
        }}
        onClick={() => hasDetails && setExpanded((e) => !e)}
      >
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
          {new Date(created).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: 12 }} noWrap>{nameCol}</Typography>
        <Typography variant="body2" sx={{ fontSize: 12 }} noWrap>{subCol}</Typography>
        <Chip
          label={method}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: 11, height: 22, fontFamily: 'monospace', borderColor: `${methodColor}33`, color: methodColor }}
        />
        <Typography variant="body2" sx={{ fontSize: 12, fontFamily: 'monospace' }} noWrap>{requestUrl ?? '-'}</Typography>
        <Typography variant="body2" sx={{ fontSize: 12 }}>{responseTimeMs != null ? `${responseTimeMs}ms` : '-'}</Typography>
        <Chip
          label={isSuccess ? 'OK' : 'Error'}
          size="small"
          sx={{
            fontWeight: 600, fontSize: 11, height: 22, border: 'none',
            bgcolor: isSuccess ? xxllncColor.opGreen10 : xxllncColor.opRed10,
            color: isSuccess ? xxllncColor.green100 : xxllncColor.red100,
          }}
        />
        {hasDetails ? (
          <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
        ) : <Box />}
      </Box>
      {expanded && (
        <Box sx={{ px: 3, py: 2, bgcolor: 'rgba(0,0,0,0.02)', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          <Box sx={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 2, mb: responseBodyPreview ? 2 : 0,
          }}>
            {statusCode != null && (
              <Box>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="body2" fontWeight={600} color={isSuccess ? 'success.main' : 'error.main'}>{statusCode}</Typography>
              </Box>
            )}
            {errorMessage && (
              <Box>
                <Typography variant="caption" color="text.secondary">Error</Typography>
                <Typography variant="body2" color="error.main">{errorMessage}</Typography>
              </Box>
            )}
          </Box>
          {responseBodyPreview && <LogBodyPreview preview={responseBodyPreview} />}
        </Box>
      )}
    </Box>
  )
}

interface OrgLogsTableProps<T> {
  data: T[]
  headers: string[]
  renderRow: (row: T, i: number) => React.ReactNode
  isLoading: boolean
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function OrgLogsTable<T>({ data, headers, renderRow, isLoading }: OrgLogsTableProps<T>) {
  const translate = useTranslate()
  const [page, setPage] = useState(1)
  const PER_PAGE = 25
  const paged = data.slice((page - 1) * PER_PAGE, page * PER_PAGE)
  const totalPages = Math.ceil(data.length / PER_PAGE)

  if (isLoading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>

  return (
    <>
      <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid', gridTemplateColumns: '140px 1fr 1fr 80px 1fr 70px 60px 32px',
          px: 2, py: 1, gap: 1, bgcolor: 'rgba(0,0,0,0.03)', borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}>
          {headers.map((h, i) => (
            <Typography key={i} variant="caption" fontWeight={600} color="text.secondary">{h}</Typography>
          ))}
        </Box>
        {paged.map((row, i) => renderRow(row, i))}
        {data.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">{translate('logs.noLogs')}</Typography>
          </Box>
        )}
      </Box>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <Box
              key={p}
              onClick={() => setPage(p)}
              sx={{
                'px': 1.5, 'py': 0.5, 'borderRadius': '4px', 'cursor': 'pointer',
                'fontSize': 12, 'fontWeight': p === page ? 700 : 400,
                'bgcolor': p === page ? xxllncColor.opDark10 : 'transparent',
                '&:hover': { bgcolor: xxllncColor.opDark10 },
              }}
            >
              {p}
            </Box>
          ))}
        </Box>
      )}
    </>
  )
}

const OrgLogsTab: FC = () => {
  const record = useRecordContext<OrganizationOut>()
  const translate = useTranslate()
  const [logType, setLogType] = useState<'mcp' | 'workflow'>('mcp')

  const { data: mcpData, isLoading: mcpLoading } = useGetList<McpUsageLogOut>(
    'mcp_usage_log',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'created', order: 'DESC' },
      // eslint-disable-next-line camelcase
      filter: { organization_id: record?.id },
    },
    { enabled: !!record?.id }
  )

  const { data: workflowData, isLoading: workflowLoading } = useGetList<WorkflowExecutionOut>(
    'workflow_execution',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'created', order: 'DESC' },
      // eslint-disable-next-line camelcase
      filter: { organization_id: record?.id },
    },
    { enabled: !!record?.id }
  )

  const mcpHeaders = [
    translate('logs.time'), translate('data.mcpServer'), translate('data.username'),
    translate('data.httpMethod'), 'URL', 'ms', translate('data.success'), '',
  ]

  const workflowHeaders = [
    translate('logs.time'),
    translate('data.workflow'),
    translate('data.triggeredBy'),
    translate('data.triggerType'),
    translate('data.status'),
    '',
    translate('data.success'),
    '',
  ]

  return (
    <>
      <LogsChart data={mcpData} isLoading={mcpLoading} />

      <ToggleButtonGroup
        value={logType}
        exclusive
        onChange={(_, v: 'mcp' | 'workflow' | null) => { if (v) setLogType(v) }}
        size="small"
        sx={{
          'mb': 2,
          '& .MuiToggleButton-root': {
            'textTransform': 'none', 'fontWeight': 600, 'fontSize': 12, 'px': 2, 'py': 0.5,
            'border': '1px solid rgba(0,0,0,0.08)', 'color': 'rgba(0,0,0,0.4)',
            '&.Mui-selected': {
              'bgcolor': xxllncColor.opDark10, 'color': xxllncColor.dark100,
              'borderColor': xxllncColor.opDark20,
              '&:hover': { bgcolor: xxllncColor.opDark20 },
            },
          },
        }}
      >
        <ToggleButton value="mcp">MCP ({mcpData?.length ?? 0})</ToggleButton>
        <ToggleButton value="workflow">Workflow ({workflowData?.length ?? 0})</ToggleButton>
      </ToggleButtonGroup>

      {logType === 'mcp' ? (
        <OrgLogsTable<McpUsageLogOut>
          data={mcpData ?? []}
          headers={mcpHeaders}
          isLoading={mcpLoading}
          renderRow={(log) => (
            <LogRowBase
              key={log.id}
              isSuccess={log.is_success}
              created={String(log.created)}
              method={log.http_method}
              responseTimeMs={log.response_time_ms}
              nameCol={log.server_name}
              subCol={log.username}
              requestUrl={log.request_url}
              statusCode={log.response_status_code}
              errorMessage={log.error_message}
              responseBodyPreview={log.response_body_preview}
            />
          )}
        />
      ) : (
        <OrgLogsTable<WorkflowExecutionOut>
          data={workflowData ?? []}
          headers={workflowHeaders}
          isLoading={workflowLoading}
          renderRow={(execution) => (
            <LogRowBase
              key={execution.id}
              isSuccess={execution.status === 'completed'}
              created={String(execution.started_at ?? execution.id)}
              method={execution.trigger_type}
              responseTimeMs={null}
              nameCol={execution.workflow_display_name}
              subCol={execution.triggered_by}
              requestUrl={execution.status}
              statusCode={execution.status === 'completed' ? 200 : 500}
              errorMessage={execution.error}
              responseBodyPreview={null}
            />
          )}
        />
      )}
    </>
  )
}


const BeheerderChip: FC<{ isBeheerder: boolean }> = ({ isBeheerder }) => {
  const translate = useTranslate()
  if (!isBeheerder) return <>{'—'}</>
  return (
    <Chip
      label={translate('orgMembers.beheerder')}
      size="small"
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 600,
        border: 'none',
        bgcolor: xxllncColor.opBlue10,
        color: xxllncColor.blue100,
      }}
    />
  )
}

const MemberActionMenu: FC<{
  member: OrgAuth0MemberOut
  orgId: string
  beheerderRoleId: string | null
  isBeheerder: boolean
  onRefresh: () => void
}> = ({ member, orgId, beheerderRoleId, isBeheerder, onRefresh }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const translate = useTranslate()
  const notify = useNotify()

  const handleAssignSeat = async () => {
    setAnchorEl(null)
    try {
      await postJson(`${getUrl()}/organization/${orgId}/members/${member.user_id}/seat`, {
        // eslint-disable-next-line camelcase
        auth0_email: member.email,
      })
      notify(translate('orgMembers.seatAssigned'), { type: 'success' })
      onRefresh()
    } catch {
      notify(translate('orgMembers.actionFailed'), { type: 'error' })
    }
  }

  const handleRemoveSeat = async () => {
    setAnchorEl(null)
    try {
      await deleteJson(`${getUrl()}/organization/${orgId}/members/${member.user_id}/seat`)
      notify(translate('orgMembers.seatRemoved'), { type: 'success' })
      onRefresh()
    } catch {
      notify(translate('orgMembers.actionFailed'), { type: 'error' })
    }
  }

  const handleAssignBeheerder = async () => {
    setAnchorEl(null)
    if (!beheerderRoleId) return
    try {
      await postJson(`${getUrl()}/organization/${orgId}/members/${member.user_id}/roles`, {
        // eslint-disable-next-line camelcase
        role_id: beheerderRoleId,
      })
      notify(translate('orgMembers.beheerderAssigned'), { type: 'success' })
      onRefresh()
    } catch {
      notify(translate('orgMembers.actionFailed'), { type: 'error' })
    }
  }

  const handleRemoveBeheerder = async () => {
    setAnchorEl(null)
    if (!beheerderRoleId) return
    try {
      await deleteJson(
        `${getUrl()}/organization/${orgId}/members/${member.user_id}/roles/${beheerderRoleId}`,
      )
      notify(translate('orgMembers.beheerderRemoved'), { type: 'success' })
      onRefresh()
    } catch {
      notify(translate('orgMembers.actionFailed'), { type: 'error' })
    }
  }

  const handleInstallInChat = async () => {
    setAnchorEl(null)
    try {
      await postJson(
        `${getUrl()}/organization/${orgId}/members/${member.user_id}/chat-install`,
        {},
      )
      notify(translate('orgMembers.chatInstalled'), { type: 'success' })
      onRefresh()
    } catch {
      notify(translate('orgMembers.actionFailed'), { type: 'error' })
    }
  }

  const handleRemoveFromChat = async () => {
    setAnchorEl(null)
    try {
      await deleteJson(
        `${getUrl()}/organization/${orgId}/members/${member.user_id}/chat-install`,
      )
      notify(translate('orgMembers.chatRemoved'), { type: 'success' })
      onRefresh()
    } catch {
      notify(translate('orgMembers.actionFailed'), { type: 'error' })
    }
  }

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {member.has_seat ? (
          <MenuItem onClick={() => { void handleRemoveSeat() }}>
            {translate('orgMembers.removeSeat')}
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { void handleAssignSeat() }}>
            {translate('orgMembers.assignSeat')}
          </MenuItem>
        )}
        {beheerderRoleId && (
          isBeheerder ? (
            <MenuItem onClick={() => { void handleRemoveBeheerder() }}>
              {translate('orgMembers.removeBeheerder')}
            </MenuItem>
          ) : (
            <MenuItem onClick={() => { void handleAssignBeheerder() }}>
              {translate('orgMembers.makeBeheerder')}
            </MenuItem>
          )
        )}
        {member.has_seat && (
          member.chat_enabled ? (
            <MenuItem onClick={() => { void handleRemoveFromChat() }}>
              {translate('orgMembers.removeFromChat')}
            </MenuItem>
          ) : (
            <MenuItem onClick={() => { void handleInstallInChat() }}>
              {translate('orgMembers.installInChat')}
            </MenuItem>
          )
        )}
      </Menu>
    </>
  )
}

const Auth0MembersTab: FC = () => {
  const record = useRecordContext<OrganizationOut>()
  const translate = useTranslate()
  const [members, setMembers] = useState<OrgAuth0MemberOut[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)

  const { data: settingsData } = useGetList<PortalSettingsOut>('portal_settings', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'created', order: 'DESC' },
  })
  const beheerderRoleId = settingsData?.[0]?.beheerder_role_id ?? null

  const handleRefresh = useCallback(() => setRefreshKey((k) => k + 1), [])

  useEffect(() => {
    if (!record?.id) return
    const abortController = new AbortController()
    setLoading(true)
    getJson<OrgAuth0MemberOut[]>(
      `${getUrl()}/organization/${record.id}/auth0-members`,
      { signal: abortController.signal },
    )
      .then(setMembers)
      .catch(() => { if (!abortController.signal.aborted) setMembers([]) })
      .finally(() => { if (!abortController.signal.aborted) setLoading(false) })
    return () => abortController.abort()
  }, [record?.id, refreshKey])

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (!record?.idp_org_id) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        {translate('orgMembers.noAuth0Org')}
      </Alert>
    )
  }

  if (members.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        {translate('orgMembers.noMembers')}
      </Alert>
    )
  }

  return (
    <Box sx={{ py: 1 }}>
      <Box
        component="table"
        sx={{
          'width': '100%',
          'borderCollapse': 'collapse',
          '& th': {
            textAlign: 'left',
            py: 1.5,
            px: 2,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: border.thin,
          },
          '& td': {
            py: 1.5,
            px: 2,
            fontSize: '0.85rem',
            borderBottom: border.light,
          },
        }}
      >
        <thead>
          <tr>
            <th>{translate('orgMembers.name')}</th>
            <th>{translate('orgMembers.email')}</th>
            <th>{translate('orgMembers.beheerder')}</th>
            <th>{translate('orgMembers.seat')}</th>
            <th>{translate('orgMembers.chat')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const isBeheerder = !!(beheerderRoleId && m.role_ids?.includes(beheerderRoleId))
            return (
              <tr key={m.user_id}>
                <td>{m.name || '—'}</td>
                <td>
                  <Typography
                    component="span"
                    sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                  >
                    {m.email || '—'}
                  </Typography>
                </td>
                <td>
                  <BeheerderChip isBeheerder={isBeheerder} />
                </td>
                <td>
                  <Chip
                    label={
                      m.has_seat
                        ? translate('orgMembers.seatYes')
                        : translate('orgMembers.seatNo')
                    }
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 600,
                      border: 'none',
                      bgcolor: m.has_seat
                        ? xxllncColor.opGreen10
                        : xxllncColor.opRed10,
                      color: m.has_seat
                        ? xxllncColor.green100
                        : xxllncColor.red100,
                    }}
                  />
                </td>
                <td>
                  {m.has_seat ? (
                    <Chip
                      label={
                        m.chat_enabled
                          ? translate('orgMembers.chatOn')
                          : translate('orgMembers.chatOff')
                      }
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        border: 'none',
                        bgcolor: m.chat_enabled
                          ? xxllncColor.opGreen10
                          : 'rgba(0,0,0,0.06)',
                        color: m.chat_enabled
                          ? xxllncColor.green100
                          : 'text.secondary',
                      }}
                    />
                  ) : '—'}
                </td>
                <td>
                  <MemberActionMenu
                    member={m}
                    orgId={String(record.id)}
                    beheerderRoleId={beheerderRoleId}
                    isBeheerder={isBeheerder}
                    onRefresh={handleRefresh}
                  />
                </td>
              </tr>
            )
          })}
        </tbody>
      </Box>
    </Box>
  )
}

const DetailsTab: FC = () => {
  const record = useRecordContext<OrganizationOut>()
  const translate = useTranslate()
  if (!record) return null

  const metadataEntries = record.metadata_fields
    ? Object.entries(record.metadata_fields)
    : []

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ mb: 3, animation: `${fadeSlideIn} 0.4s ease-out both` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: '#1a1a1a' }}>
            {record.display_name}
          </Typography>
          <TierChip tier={record.hub_tier} />
          {record.blocked_until && (
            <Chip
              icon={<BlockIcon sx={{ fontSize: 14 }} />}
              label={translate('data.blockedUntil', { date: formatDateTime(record.blocked_until) })}
              size="small"
              sx={{ fontWeight: 600, fontSize: 11, height: 22, border: 'none', bgcolor: xxllncColor.opRed10, color: xxllncColor.red100 }}
            />
          )}
        </Box>
        {record.description && (
          <Typography sx={{ fontSize: 14, color: 'rgba(0,0,0,0.55)', lineHeight: 1.5, maxWidth: 600 }}>
            {record.description}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
        <StatCard
          value={record.mcp_usage_total ?? 0}
          label={translate('data.mcpCalls')}
          accent={xxllncColor.dark100}
          delay={80}
        />
        <StatCard
          value={record.workflow_executions_total ?? 0}
          label={translate('data.workflowExecutions')}
          accent={xxllncColor.orange100}
          delay={120}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: metadataEntries.length > 0 ? '1fr 1fr' : '1fr', gap: 3 }}>
        <DetailCard title={translate('data.details')} delay={240}>
          <InfoRow label={translate('data.name')} value={record.name} mono />
          <InfoRow label={translate('data.auth0OrgId')} value={record.idp_org_id} mono />
          <InfoRow label={translate('data.createdAt')} value={record.created ? formatDateTime(String(record.created)) : undefined} />
          <InfoRow
            label={translate('data.lastModified')}
            value={record.lastModified ? formatDateTime(String(record.lastModified)) : undefined}
          />
        </DetailCard>

        {metadataEntries.length > 0 && (
          <DetailCard title={translate('data.metadataFields')} delay={300}>
            <MetadataTable entries={metadataEntries} />
          </DetailCard>
        )}
      </Box>
    </Box>
  )
}

const DeletedBanner: FC = () => {
  const record = useRecordContext<OrganizationOut>()
  const translate = useTranslate()
  if (!record?.deleted) return null
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      {translate('data.deletedBanner')}
    </Alert>
  )
}

const OrganizationShow: FC = () => {
  const translate = useTranslate()

  return (
    <Show actions={<ShowActions />}>
      <DeletedBanner />
      <TabbedShowLayout>
        <Tab label="resources.organization.tabs.details">
          <DetailsTab />
        </Tab>
        <Tab label="resources.organization.tabs.users" path="users">
          <Auth0MembersTab />
        </Tab>
        <Tab label="resources.organization.tabs.links" path="links">
          <LinksTab />
        </Tab>
        <Tab label="resources.organization.tabs.usage" path="usage">
          <UsageTab />
        </Tab>
        <Tab label="resources.organization.tabs.automations" path="automations">
          <AutomationsTab />
        </Tab>
        <Tab label="resources.organization.tabs.chatConfig" path="chat-config">
          <OrgOpenWebuiConfigTab />
        </Tab>
        <Tab label="resources.organization.tabs.identityProviders" path="identity-providers">
          <OrganizationIdpTab />
        </Tab>
        <Tab label="resources.organization.tabs.logs" path="logs">
          <OrgLogsTab />
        </Tab>
      </TabbedShowLayout>
    </Show>
  )
}

export default OrganizationShow
